import { useResponse } from 'contexts/response';
import { useCallback } from 'react';

import { Location, Page, TruckingCompany } from 'types';
import { formatTruckingCompany } from 'utils/format';

interface FetchDataParams {
  pageNum?: number;
  pageSize?: number;
  filterString?: string;
  sortField?: string;
  sortDirection?: string;
}

interface ItiBaseEntity {
  createdBy: string;
  createdDate: Date | number | string;
  lastUpdatedBy?: string | null;
  lastUpdatedDate?: Date | number | string | null;
}

export interface LocationCarrier extends ItiBaseEntity {
  id: number;
  location: Location;
  truckingCompany: TruckingCompany;
  gateInAllowed: boolean;
  gateOutAllowed: boolean;
  startDate?: string | number | null;
  notes?: string | null;
}

const isLocationCarrier = (json: any): json is LocationCarrier => {
  return (
    typeof json === 'object' &&
    typeof json.location === 'object' &&
    typeof json.truckingCompany === 'object' &&
    typeof json.gateInAllowed === 'boolean' &&
    typeof json.gateOutAllowed === 'boolean'
  );
};

const isLocationCarrierPage = (json: any): json is Page<LocationCarrier> => {
  return typeof json === 'object' && 'content' in json && Array.isArray(json.content);
};

const useLocationCarrierApi = () => {
  const { handleError, setSuccess } = useResponse();

  const fetchData = useCallback(async (args: FetchDataParams): Promise<Page<LocationCarrier>> => {
    const init: RequestInit = {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
    };

    const input = new URL('/api/location-carriers', window.location.origin);
    Object.entries(args).forEach(([param, value]) => {
      input.searchParams.append(param, `${value}`);
    });

    try {
      const response = await fetch(input, init);
      if (response.ok) {
        const json = await response.json();
        if (isLocationCarrierPage(json)) {
          return json;
        }
      }
      return { content: [] };
    } catch (_) {
      return { content: [] };
    }
  }, []);

  const handleJsonResponse = useCallback(
    async (input: RequestInfo | URL, init: RequestInit) => {
      try {
        const response = await fetch(input, init);
        const json = await response.json();
        if (response.ok && isLocationCarrier(json)) {
          setSuccess(
            <dl>
              <dt className="text-info-main">LOCATION</dt>
              <dd className="text-info-dark">{json.location?.name}</dd>
              <dt className="text-info-main">CARRIER</dt>
              <dd className="text-info-dark">{formatTruckingCompany(json.truckingCompany)}</dd>
              <dt className="text-info-main">GATE IN ALLOWED</dt>
              <dd className="text-info-dark">{json.gateInAllowed ? 'YES' : 'NO'}</dd>
              <dt className="text-info-main">GATE OUT ALLOWED</dt>
              <dd className="text-info-dark">{json.gateOutAllowed ? 'YES' : 'NO'}</dd>
            </dl>
          );
          return json;
        }
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
        return null;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const createRule = useCallback(
    async (rule: Partial<LocationCarrier>): Promise<LocationCarrier | null> => {
      if (!isLocationCarrier(rule)) {
        handleError('Invalid rule');
        return null;
      }
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(rule)
      };
      const input = new URL('/api/location-carriers', window.location.origin);
      return await handleJsonResponse(input, init);
    },
    [handleError, handleJsonResponse]
  );

  const editRule = useCallback(
    async (rule?: LocationCarrier | null): Promise<LocationCarrier | null> => {
      if (!rule?.id) {
        handleError('Invalid rule');
        return null;
      }
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(rule)
      };
      const input = new URL(`/api/location-carriers/${rule.id}`, window.location.origin);
      return await handleJsonResponse(input, init);
    },
    [handleError, handleJsonResponse]
  );

  const deleteRule = useCallback(
    async (id: number) => {
      const init: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/location-carriers/${id}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        if (response.ok) {
          setSuccess(`Rule #${id} deleted`);
          return;
        }
        const json = await response.json();
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const getRule = useCallback(
    async (id: number | string): Promise<LocationCarrier | null> => {
      const init: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/location-carriers/${id}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        const json = await response.json();
        if (response.ok && isLocationCarrier(json)) {
          return json;
        }
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
        return null;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError]
  );

  return { createRule, deleteRule, editRule, fetchData, getRule };
};

export default useLocationCarrierApi;
